/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  EnhancedLocalStorage.ts
 *               Thin wrapper around local storage that auto-stringifies values
 */

import { getKey } from './helpers/getKey'
import { getStorageKey } from './helpers/getStorageKey'
import { isLocalStorageAvailable } from './helpers/isLocalStorageAvailable'
import { logger } from './logger'
import { EnhancedStorage } from './types/EnhancedStorage'
import { EnhancedStorageOptions } from './types/EnhancedStorageOptions'

export type EnhancedLocalStorageOptions = EnhancedStorageOptions
export class EnhancedLocalStorage<TValue = any>
  implements EnhancedStorage<TValue>
{
  protected storage?: Storage
  protected options: EnhancedLocalStorageOptions

  constructor(options: EnhancedLocalStorageOptions) {
    this.options = { ...options }
    this.storage = isLocalStorageAvailable()
      ? (global as unknown as Window).localStorage
      : undefined
  }

  get length() {
    return this.storage?.length ?? 0
  }

  clear() {
    this.storage?.clear()
  }

  getItem(key: string): TValue | null {
    const data = this.storage?.getItem(getStorageKey(key, this.options))
    if (data === undefined || data === null || data === 'undefined') {
      return null
    }
    try {
      return JSON.parse(data)
    } catch (error) {
      logger.debug(data)
      logger.error(error)
      return null
    }
  }

  key(index: number) {
    const storageKey = this.storage?.key(index) ?? null
    return storageKey !== null ? getKey(storageKey, this.options) : null
  }

  removeItem(key: string) {
    this.storage?.removeItem(getStorageKey(key, this.options))
  }

  setItem(key: string, value: TValue) {
    try {
      const data = JSON.stringify(value)
      this.storage?.setItem(getStorageKey(key, this.options), data)
    } catch (error) {
      logger.error(error)
    }
  }
}
